import { useState, useMemo, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsPeople, BsBoxArrowUpRight, BsTrash, BsDownload, BsSlashCircle } from 'react-icons/bs';

import TransferContactsToCrmModal from '#crmFeature/components/leads/TransferContactsToCrmModal/TransferContactsToCrmModal';
import CustomColumnDialogs from '#customColumnsFeature/components/CustomColumnDialogs/CustomColumnDialogs';
import { useCustomColumnsTable, withCustomColumns } from '#customColumnsFeature/hooks/useCustomColumnsTable';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import Table from '#globalComponents/Table/Table';
import TableFilterToolbar from '#globalComponents/Table/TableFilterToolbar';
import { useClampPage } from '#globalHooks/useClampPage';
import { useScopedSelection } from '#globalHooks/useScopedSelection';
import { CONTACT_ENTITY_LABEL } from '#lib/constants/entityLabels';

import BlacklistSelectionModal from '../../components/BlacklistSelectionModal/BlacklistSelectionModal';
import ContactListEditModal from '../../components/ContactListEditModal/ContactListEditModal';
import { LIST_RECIPIENT_FILTER_SCHEMA, LIST_RECIPIENTS_PAGE_SIZE } from '../../constants/tableFilters';
import { useRowHighlight } from '../../hooks/useRowHighlight';
import { useRemoveContactsFromList } from '../../models/mutations';
import { CONTACT_SYSTEM_COLUMNS } from '../systemColumns';

import { buildContactColumns } from './contactColumns';

const EMPTY_ROWS = [];

const getContactId = (row) => row.crmContactId;
const getRecipientRef = (row) => ({ crmContactId: row.crmContactId });
const getContactLabel = (row) => row.crmContacts.fullName || row.crmContacts.emailAddress || 'Kontakt';
const toBlacklistEntry = (row) =>
	row.crmContacts?.emailAddress ? { entryType: 'email', value: row.crmContacts.emailAddress } : null;

const formatCount = (count) => count.toLocaleString('de-DE');

/**
 * Contacts tab of the contact list detail page: server-paginated, filterable table with
 * scoped selection, bulk CRM transfer, bulk remove, inline edit, and per-row remove.
 */
const ContactListContactsTab = ({ listId, hasCrm, urlFilters, query, highlightId, onHighlightConsumed }) => {
	const navigate = useNavigate();
	const removeContactsMutation = useRemoveContactsFromList();
	const { filters, filtersKey, setFilters, page, setPage } = urlFilters;

	const rows = query.data?.contacts ?? EMPTY_ROWS;
	const total = query.data?.total ?? 0;
	const listTotal = query.data?.stats?.total ?? 0;
	const totalPages = query.data?.totalPageCount ?? 1;

	const [removeTarget, setRemoveTarget] = useState(null);
	const [blacklistRequest, setBlacklistRequest] = useState(null);
	const [editTarget, setEditTarget] = useState(null);
	const [editInitialTab, setEditInitialTab] = useState('contact');
	const [transferRequest, setTransferRequest] = useState(null);

	useClampPage({ page, totalPages, setPage, isReady: !query.isFetching });

	useRowHighlight({
		listId,
		kind: 'contacts',
		highlightId,
		settled: !query.isFetching,
		rows,
		getRowId: getContactId,
		urlFilters,
		onConsumed: onHighlightConsumed,
	});

	const selection = useScopedSelection(rows, {
		totalCount: total,
		filters,
		resetKey: `${listId}|${filtersKey}`,
		rowId: getContactId,
		entityLabel: CONTACT_ENTITY_LABEL,
	});

	const openBlacklist = () => {
		const selectedRows = rows.filter((row) => selection.selectedIds.includes(getContactId(row)));
		setBlacklistRequest({
			entries: selectedRows.map(toBlacklistEntry).filter(Boolean),
			selectedCount: selectedRows.length,
			withoutValueCount: selectedRows.filter((row) => !toBlacklistEntry(row)).length,
		});
	};

	const customColumns = useCustomColumnsTable({
		listId,
		table: 'contactListContacts',
		rows,
		selection,
		getRecipientRef,
		getRowLabel: getContactLabel,
	});

	// Only the loaded page is known, so the hint counts this company's contacts on the page.
	const companyContactCount = useMemo(() => {
		const companyId = editTarget?.crmContacts?.crmCompanies?.id;
		if (!companyId) return 0;
		return rows.filter((row) => row.crmContacts?.crmCompanies?.id === companyId).length;
	}, [editTarget, rows]);

	const openEdit = useCallback((row, tab) => {
		setEditTarget(row);
		setEditInitialTab(tab);
	}, []);

	const columns = useMemo(
		() =>
			withCustomColumns(
				buildContactColumns({
					filters,
					onFilterChange: setFilters,
					hasCrm,
					onEdit: openEdit,
					onOpenCompany: (companyId) => navigate(`/crm/companies/${companyId}`),
					onRemove: (row) => setRemoveTarget({ scope: { ids: [row.crmContactId] }, count: 1 }),
				}),
				[...CONTACT_SYSTEM_COLUMNS, ...customColumns.columns],
			),
		[filters, setFilters, hasCrm, openEdit, navigate, customColumns.columns],
	);

	const openTransfer = () => setTransferRequest({ scope: selection.toActionScope(), count: selection.actionCount });

	const handleConfirmRemove = async () => {
		if (!removeTarget) return;
		await removeContactsMutation.mutateAsync({ listId, scope: removeTarget.scope });
		setRemoveTarget(null);
		selection.clear();
	};

	if (query.isLoading) {
		return <p className="text-sm text-bgBase-800 py-8 text-center">Wird geladen ...</p>;
	}

	if (listTotal === 0) {
		return (
			<div className="flex flex-col items-center gap-3 py-16 text-center">
				<BsPeople className="w-8 h-8 text-bgBase-800" />
				<h3 className="text-sm font-semibold text-text">Keine Kontakte in dieser Liste</h3>
				<p className="text-xs text-bgBase-800 max-w-sm">Fügen Sie Kontakte über die Lead-Übersicht hinzu.</p>
			</div>
		);
	}

	const removeCount = removeTarget?.count ?? 0;

	return (
		<>
			<CustomColumnDialogs dialogs={customColumns.dialogs} scopeLabel={selection.actionScopeLabel} />
			<TableFilterToolbar
				urlFilters={urlFilters}
				schema={LIST_RECIPIENT_FILTER_SCHEMA}
				searchPlaceholder="Name, E-Mail oder Firma suchen …"
				resultLabel={`${formatCount(total)} von ${formatCount(listTotal)} Kontakten`}
			/>
			<Table
				rows={rows}
				columns={columns}
				rowId={getContactId}
				layoutKey="contactListContacts"
				onAddColumn={customColumns.dialogs.openCreate}
				addColumnLabel="Eigene Spalte hinzufügen"
				highlightedId={highlightId ?? undefined}
				emptyLabel="Keine Kontakte entsprechen den aktiven Filtern"
				getRowProps={(row) => ({ id: `list-row-${row.crmContactId}` })}
				selection={selection.tableSelection}
				actions={{
					label: selection.actionScopeLabel,
					buttons: (
						<>
							<ActionScopeButton
								icon={<BsDownload />}
								onClick={customColumns.dialogs.openExport}
								disabled={selection.actionCount === 0}
							>
								CSV-Export
							</ActionScopeButton>
							{hasCrm && (
								<ActionScopeButton
									icon={<BsBoxArrowUpRight />}
									onClick={openTransfer}
									disabled={selection.actionCount === 0}
								>
									Als Deal anlegen
								</ActionScopeButton>
							)}
						</>
					),
				}}
				pagination={{
					currentPage: page,
					totalPages,
					onPageChange: setPage,
					totalItems: total,
					pageSize: LIST_RECIPIENTS_PAGE_SIZE,
				}}
				bulkBar={
					<BulkActionsBar
						selectedCount={selection.count}
						entityLabel={selection.entityLabel}
						onClear={selection.clear}
						actions={[
							{
								key: 'blacklist',
								icon: <BsSlashCircle />,
								label: 'Auf Blacklist setzen',
								disabled: selection.selectAllInFilter,
								title: selection.selectAllInFilter ? 'Nur für einzeln ausgewählte Einträge' : undefined,
								onClick: openBlacklist,
							},
							{
								key: 'remove',
								icon: <BsTrash />,
								label: 'Entfernen',
								onClick: () => setRemoveTarget({ scope: selection.toScope(), count: selection.count }),
							},
						]}
					/>
				}
			/>

			<ConfirmModal
				isOpen={Boolean(removeTarget)}
				onClose={() => setRemoveTarget(null)}
				onConfirm={handleConfirmRemove}
				title={removeCount === 1 ? 'Kontakt entfernen' : `${formatCount(removeCount)} Kontakte entfernen`}
				message={
					removeCount === 1
						? 'Möchten Sie diesen Kontakt aus der Liste entfernen?'
						: `Möchten Sie ${formatCount(removeCount)} Kontakte aus der Liste entfernen?`
				}
				confirmText="Entfernen"
				cancelText="Abbrechen"
				isLoading={removeContactsMutation.isPending}
			/>

			{blacklistRequest && (
				<BlacklistSelectionModal
					entryType="email"
					entries={blacklistRequest.entries}
					selectedCount={blacklistRequest.selectedCount}
					withoutValueCount={blacklistRequest.withoutValueCount}
					onClose={() => setBlacklistRequest(null)}
					onDone={selection.clear}
				/>
			)}

			<ContactListEditModal
				isOpen={Boolean(editTarget)}
				onClose={() => setEditTarget(null)}
				contact={editTarget?.crmContacts}
				company={editTarget?.crmContacts?.crmCompanies}
				initialTab={editInitialTab}
				companyContactCount={companyContactCount}
				onSaveSuccess={() => query.refetch()}
			/>

			{hasCrm && transferRequest && (
				<TransferContactsToCrmModal
					isOpen
					onClose={() => setTransferRequest(null)}
					tableScope={{ table: 'contactListContacts', contextId: listId, ...transferRequest.scope }}
					count={transferRequest.count}
				/>
			)}
		</>
	);
};

ContactListContactsTab.propTypes = {
	listId: PropTypes.string.isRequired,
	hasCrm: PropTypes.bool,
	// Result of useUrlFilters(LIST_RECIPIENT_FILTER_SCHEMA, { namespace: 'contacts' }).
	urlFilters: PropTypes.object.isRequired,
	// useContactListContacts result for the current page + filters.
	query: PropTypes.shape({
		data: PropTypes.object,
		isLoading: PropTypes.bool,
		isFetching: PropTypes.bool,
		refetch: PropTypes.func.isRequired,
	}).isRequired,
	highlightId: PropTypes.string,
	onHighlightConsumed: PropTypes.func.isRequired,
};

export default ContactListContactsTab;
